import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type Medcall2IdentityAlias = {
  firstname?: string | null;
  lastname?: string | null;
};

export type Medcall2IdentityHistory = {
  date?: string | null;
  deactivation_reason?: string | null;
  fullname?: string | null;
  status?: string | null;
  username?: string | null;
};

export type Medcall2ParticipantIdentity = {
  active: boolean;
  activationHistory: Medcall2IdentityHistory[];
  addedAt?: string | null;
  aliases?: Medcall2IdentityAlias[] | null;
  cid?: string | null;
  dob?: string | null;
  facility?: string | null;
  firstname?: string | null;
  isNurselessProgram: boolean;
  lastname?: string | null;
  nurse?: string | null;
  participantId?: number | null;
  program?: string | null;
  provider?: string | null;
  psychiatrist?: string | null;
  staffAssigned?: string | null;
};

const normalizeText = (value: unknown) => `${value ?? ''}`.trim();

const LegacyStatusIcon = ({ active }: { active: boolean }) => (
  <svg aria-hidden="true" height="13" viewBox="0 0 13 13" width="13">
    <path d="M11.5086 3.07129H4.20577C3.69766 3.07129 3.28577 3.48319 3.28577 3.99129V11.2941C3.28577 11.8022 3.69766 12.2141 4.20577 12.2141H11.5086C12.0167 12.2141 12.4286 11.8022 12.4286 11.2941V3.99129C12.4286 3.48319 12.0167 3.07129 11.5086 3.07129Z" fill="none" stroke={active ? '#000' : '#fff'} strokeMiterlimit="10" />
    <path d="M2.14286 9.9285H1.92C1.41143 9.9285 1 9.51707 1 9.0085V1.70564C1 1.19707 1.41143 0.785645 1.92 0.785645H9.22286C9.73143 0.785645 10.1429 1.19707 10.1429 1.70564V1.9285" fill="none" stroke={active ? '#000' : '#fff'} strokeMiterlimit="10" />
    <path d="M7.26855 4.76855H10.7314V8.23141M7.46875 8.03141L10.7316 4.76855" fill="none" stroke={active ? '#000' : '#fff'} strokeMiterlimit="10" />
  </svg>
);

export const formatMedcall2LegacyIdentityDate = (
  value: string | null | undefined,
  includeTime = false,
  twoDigitYear = false,
) => {
  const normalizedValue = normalizeText(value);
  const match = normalizedValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?/,
  );
  if (!match) {
    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return normalizedValue;
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      hour: includeTime ? 'numeric' : undefined,
      hour12: true,
      minute: includeTime ? '2-digit' : undefined,
      month: '2-digit',
      timeZone: 'America/Los_Angeles',
      year: twoDigitYear ? '2-digit' : 'numeric',
    }).formatToParts(parsedDate);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value || '';
    const dateLabel = `${part('month')}/${part('day')}/${part('year')}`;
    if (!includeTime) {
      return dateLabel;
    }

    return `${dateLabel} ${part('hour')}:${part('minute')} ${part('dayPeriod')}`;
  }

  const year = twoDigitYear ? match[1].slice(-2) : match[1];
  const dateLabel = `${match[2]}/${match[3]}/${year}`;
  if (!includeTime || !match[4] || !match[5]) {
    return dateLabel;
  }

  const hour = Number(match[4]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${dateLabel} ${displayHour}:${match[5]} ${suffix}`;
};

export const getMedcall2LegacyAliasLabel = (
  aliases: Medcall2IdentityAlias[] | null | undefined,
) => {
  if (!Array.isArray(aliases)) {
    return '';
  }

  return aliases
    .map((alias) => [normalizeText(alias.firstname), normalizeText(alias.lastname)]
      .filter(Boolean)
      .join(' '))
    .filter(Boolean)
    .join(', ');
};

const IdentityField = ({
  children,
  className,
  label,
}: {
  children: React.ReactNode;
  className: string;
  label: string;
}) => (
  <div className={`medcall2-participant-identity__field ${className}`} data-testid="medcall2-identity-field">
    <span className="medcall2-participant-identity__label">{label}</span>
    <span className="medcall2-participant-identity__value">{children}</span>
  </div>
);

export const Medcall2ParticipantIdentityCard = ({
  actions,
  onBack,
  participant,
}: {
  actions: React.ReactNode;
  onBack: () => void;
  participant: Medcall2ParticipantIdentity;
}) => {
  const [statusHistoryOpen, setStatusHistoryOpen] = React.useState(false);
  const aliasLabel = getMedcall2LegacyAliasLabel(participant.aliases);
  const clientName = [normalizeText(participant.firstname), normalizeText(participant.lastname)]
    .filter(Boolean)
    .join(' ');
  const statusLabel = participant.active ? 'Client Active' : 'Client Deactivated';

  return (
    <>
      <div className="medcall2-participant-view">
        <div className="medcall2-participant-actions-row">{actions}</div>
        <section
          className={`medcall2-participant-identity ${participant.isNurselessProgram ? 'medcall2-participant-identity--four-columns' : ''}`}
          data-testid="medcall2-participant-identity"
        >
        <header className={`medcall2-participant-identity__header ${participant.active ? '' : 'medcall2-participant-identity__header--inactive'}`}>
          <button
            aria-label="Back"
            className="medcall2-participant-identity__back"
            onClick={onBack}
            title="Back"
            type="button"
          >
            <ChevronLeft aria-hidden="true" size={28} />
          </button>
          <div className="medcall2-participant-identity__title">
            <strong className="medcall2-participant-identity__name">{clientName}</strong>
            <span
              aria-label={participant.active ? 'CLIENT ACTIVE' : 'CLIENT DEACTIVATED'}
              className={`medcall2-participant-identity__status-dot ${participant.active ? 'medcall2-participant-identity__status-dot--active' : 'medcall2-participant-identity__status-dot--inactive'}`}
              role="img"
              title={participant.active ? 'CLIENT ACTIVE' : 'CLIENT DEACTIVATED'}
            />
            {aliasLabel ? (
              <span className="medcall2-participant-identity__aliases">{aliasLabel}</span>
            ) : null}
          </div>
        </header>

        <div className="medcall2-participant-identity__grid" data-testid="medcall2-identity-grid">
          <IdentityField className="medcall2-participant-identity__field--id" label="ID">
            {participant.participantId ?? ''}
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--program" label="Program">
            {normalizeText(participant.program)}
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--added" label="Added at">
            {formatMedcall2LegacyIdentityDate(participant.addedAt, true, true)}
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--dob" label="DOB">
            {formatMedcall2LegacyIdentityDate(participant.dob, false, true)}
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--cid" label="CID">
            <Link id="medcall-participant-cid" className="medcall2-participant-identity__cid" to="/search" state={{ searchQuery: clientName }}>
                {normalizeText(participant.cid)}
            </Link>
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--provider" label="Provider">
            {normalizeText(participant.provider)}
          </IdentityField>
          {!participant.isNurselessProgram ? (
            <IdentityField className="medcall2-participant-identity__field--facility" label="Facility">
              {normalizeText(participant.facility) || '-'}
            </IdentityField>
          ) : null}
          <IdentityField className="medcall2-participant-identity__field--psychiatrist" label="Psychiatrist">
            {normalizeText(participant.psychiatrist) || 'N/A'}
          </IdentityField>
          <IdentityField className="medcall2-participant-identity__field--staff" label="Staff Assigned">
            {normalizeText(participant.staffAssigned)}
          </IdentityField>
          {!participant.isNurselessProgram ? (
            <IdentityField className="medcall2-participant-identity__field--nurse" label="Nurse">
              {normalizeText(participant.nurse)}
            </IdentityField>
          ) : null}
          <div className="medcall2-participant-identity__field medcall2-participant-identity__field--status" data-testid="medcall2-identity-field">
            <span className="medcall2-participant-identity__label">Status</span>
            <span
              className={`medcall2-participant-identity__status ${participant.active ? 'medcall2-participant-identity__status--active' : 'medcall2-participant-identity__status--inactive'}`}
              onClick={() => setStatusHistoryOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setStatusHistoryOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
              title="Click for Status History"
            >
              {statusLabel}
              <LegacyStatusIcon active={participant.active} />
            </span>
          </div>
        </div>
        </section>
      </div>

      <Dialog open={statusHistoryOpen} onOpenChange={setStatusHistoryOpen}>
        <DialogContent
          className="medcall2-participant-status-dialog max-w-4xl"
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth="900px"
          contentWidth="900px"
        >
          <DialogHeader>
            <DialogTitle>History Client Status</DialogTitle>
            <DialogDescription className="sr-only">
              Client activation and deactivation history.
            </DialogDescription>
          </DialogHeader>
          <div className="medcall2-participant-status-history">
            {participant.activationHistory.length < 1 ? (
              <div className="medcall2-participant-status-history__empty">No Data</div>
            ) : null}
            {participant.activationHistory.map((historyItem, index) => (
              <div className="medcall2-participant-status-history__row" key={`${historyItem.date || 'status'}-${index + 1}`}>
                <div>
                  {normalizeText(historyItem.fullname)}
                  {normalizeText(historyItem.username) ? ` (${normalizeText(historyItem.username)})` : ''}
                </div>
                <div className="medcall2-participant-status-history__status">
                  <span>{normalizeText(historyItem.status)}</span>
                  <span>({formatMedcall2LegacyIdentityDate(historyItem.date, true)})</span>
                  {normalizeText(historyItem.status).toLowerCase() === 'deactivated' ? (
                    <span title={`Deactivation Reason: ${normalizeText(historyItem.deactivation_reason) || 'N/A'}`}>ⓘ</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
